import { css, Theme } from '@emotion/react';
import { ArrowRight } from '@phosphor-icons/react';
import { useAccount } from 'client/lib/User';
import { Button } from 'client/components/Button';
import { ButtonWithTrialBlock } from 'client/components/Button/ButtonWithTrialBlock';
import { Hero, HeroProps } from 'client/components/Hero/Hero';
import { Post } from 'client/components/Post';
import { Typography } from 'client/components/Typography';
import { Font } from 'client/Theme';

export type DashboardOnboarding = Partial<HeroProps>;

export const DashboardOnboarding = (props: DashboardOnboarding) => {
  const account = useAccount();
  const project = account?.projects[0];

  return (
    <Post outline>
      <Hero title="Let's get you up to speed" css={heroStyle} {...props}>
        <Typography>🎉 Good to have you on board! Here's a short ToDo list to get you up & running in no time:</Typography>

        <ol css={stepperStyle}>
          <li data-done={!!project}>
            <Typography>Create a Project</Typography>
            <Typography variant="body2">A project is a collection of deployments. We need to create one to get started.</Typography>
            <ButtonWithTrialBlock href={`/${account?.identifier}/template/create`} icon={<ArrowRight weight="bold" />} primary>
              Create Project
            </ButtonWithTrialBlock>
          </li>
          <li data-done={(project?.deploymentIDs.length || 0) > 0}>
            <Typography>Launch a Deployment</Typography>
            <Typography variant="body2">
              A deployment will run your app. You can try creating a development one to get to coding right away!
            </Typography>
            <Button
              href={`/${account?.identifier}/project/${project?.id}/deployment/create`}
              icon={project ? <ArrowRight weight="bold" /> : undefined}
              primary={!!project}
              disabled={!project}
            >
              Create Deployment
            </Button>
          </li>
        </ol>
      </Hero>
    </Post>
  );
};

const circleCheckIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Cpath d='M128 24a104 104 0 1 0 104 104A104.2 ` +
  `104.2 0 0 0 128 24Zm49.5 85.8-58.6 56a8.1 8.1 0 0 1-5.6 2.2 7.7 7.7 0 0 1-5.5-2.2l-29.3-28a8 8 0 1 1 11-11.6l23.8 ` +
  `22.7 53.2-50.7a8 8 0 0 1 11 11.6Z' fill='${theme.color.primary[100].replace('#', '%23')}' /%3E%3C/svg%3E");`;

const heroStyle = (theme: Theme) => css`
  margin-top: 0;
  padding: 0;

  > aside {
    display: none;
  }

  > div > header {
    margin-bottom: 8px;
  }

  > div > .typography.body1 {
    color: ${theme.color.grey[100]};
  }

  ::after {
    content: none;
  }
`;

const stepperStyle = (theme: Theme) => css`
  margin: 0;
  margin-top: 48px;
  padding: 0;
  list-style: none;
  counter-reset: stepper;

  > li {
    position: relative;
    margin-top: 32px;
    counter-increment: stepper;

    &::before {
      content: counter(stepper);
      position: absolute;
      top: -2px;
      left: 0;
      display: flex;
      justify-content: center;
      line-height: 26px;
      width: 27px;
      height: 27px;
      font-family: ${Font.header};
      font-weight: bold;
      border-radius: 50%;
      font-size: 1rem;
      border: 2px solid ${theme.color.grey[80]};
      color: ${theme.color.grey[80]};
    }

    :first-child,
    &[data-done='true'] + li {
      &::before {
        border-color: ${theme.color.primary[100]};
        color: ${theme.color.primary[100]};
      }
    }

    &[data-done='true'] {
      &::before {
        content: '';
        border-color: ${theme.color.primary[100]};
        background-size: calc(100% + 8px);
        background-position: -4px -4px;
        background-image: ${circleCheckIconURI(theme)};
        color: ${theme.color.white};
      }

      .button {
        display: none;
      }
    }

    > .body1 {
      margin-bottom: 4px;
      margin-left: calc(29px + 8px);
      font-weight: bold;
    }

    > p {
      max-width: 48ch;
    }

    .button {
      margin-top: 16px;
      width: fit-content;
    }
  }
`;
